import * as React from 'react';
import { ServiceLocator } from '../../../app/ServiceLocator';
import { TelemetryService } from '../../../ts/services/TelemetryService';
import { TelemetryEvents, UnhandledErrorTelemetryEvent } from '../../telemetryEvents/TelemetryEvents';

/**
 * A function which handles errors caught by the componentDidCatch lifecycle hook
 */
export type ErrorBoundaryHandler = (error: Error, reactInfo: React.ErrorInfo) => void;

/**
 * Creates a HOC which handles uncaught errors in the subtree of the wrapped component.
 * Usage is similar to the connect function in redux. The HOC is a stateful component because
 * enzyme didn't support simulating errors on stateless components.
 * @param handler - The error handler to use when an unhandled error is caught
 */
export function errorBoundary<PropType extends object>(handler: ErrorBoundaryHandler):
  (WrappedComponent: React.ComponentType<PropType>) => React.ComponentType<PropType> {

  // delegate the handling to the specified function, and just render child components verbatim
  class ErrorBoundary extends React.PureComponent {
    public componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
      handler(error, errorInfo);
    }

    render(): React.ReactNode {
      return this.props.children;
    }
  }

  return function wrapWithErrorBoundary<PropType>(WrappedComponent: React.ComponentType<PropType>): React.ComponentType<PropType> {
    return class extends React.PureComponent<PropType> {
      render() {
        return <ErrorBoundary><WrappedComponent {...this.props}/></ErrorBoundary>;
      }
    };
  };
}

/**
 * Sends an unhandled error telemetry event
 * @param error - The error caught by the componentDidCatch lifecycle hook. It contains the stacktrace
 * @param errorInfo - The react error info caught by the componentDidCatch lifecycle hook.
 * It contains the component tree which caused the error.
 */
export function sendTelemetryErrorEvent(error: Error, errorInfo: React.ErrorInfo): void {
  const telemetryService = ServiceLocator.get<TelemetryService>('TelemetryService');
  telemetryService.sendEvent<UnhandledErrorTelemetryEvent>({
    type: TelemetryEvents.UnhandledError,
    error: JSON.stringify({message: error && error.message, stack: error && error.stack}),
    errorInfo: errorInfo.componentStack
  });
}
